// src/lib/gemini.js
// Calls Gemini directly from the browser (no backend required).
// The API key is read from REACT_APP_GEMINI_API_KEY in your .env file.
// Works on localhost, any static host, or any custom domain.

const MODEL_NAME = "gemini-2.5-flash";
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

// Minimum wait before retrying a specific error status.
// 429 = rate limit  → Google recommends ≥60 s.
// 503 = unavailable → 15 s is usually enough.
const MIN_RETRY_DELAY_MS = {
  429: 60_000,
  503: 15_000,
};

// Max lines to include from CSV/XLSX text content to keep the request payload small.
const MAX_TEXT_ROWS = 500;

/**
 * Call Gemini directly from the browser.
 *
 * @param {string}    prompt
 * @param {string}    systemInstruction
 * @param {boolean}   isJson          - if true, request JSON output
 * @param {File|null} file            - optional file (CSV, XLSX, PDF, image)
 * @returns {Promise<string>}
 *
 * On failure throws an Error with `.status` set to the HTTP status code
 * so callers can show 429 / 503 countdown messages.
 */
export async function callGeminiProxy(
  prompt,
  systemInstruction = "",
  isJson = false,
  file = null,
) {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error(
      "Gemini API key not found. Add REACT_APP_GEMINI_API_KEY to your .env file and restart the dev server.",
    );
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_NAME}:generateContent?key=${apiKey}`;
  const parts = [{ text: prompt }];

  if (file) {
    if (file.type === "text/csv" || file.name.endsWith(".csv")) {
      // Extract text on the frontend and append directly — no base64 needed.
      const text = await readAsText(file);
      parts[0].text += `\n\nDocument Data:\n${trimRows(text, MAX_TEXT_ROWS)}`;
    } else if (file.name.match(/\.(xlsx|xls)$/i)) {
      // Convert spreadsheet to CSV text and append — no base64 needed.
      const text = await readExcelAsText(file);
      parts[0].text += `\n\nSpreadsheet Data:\n${trimRows(text, MAX_TEXT_ROWS)}`;
    } else {
      // PDF / image — upload via Gemini File API, then reference by URI.
      // This avoids stuffing the entire base64 blob into the request body.
      const mimeType = resolveMimeType(file);
      const fileUri = await uploadToGeminiFileApi(file, apiKey);
      parts.push({ fileData: { mimeType, fileUri } });
    }
  }

  const payload = {
    contents: [{ role: "user", parts }],
    ...(systemInstruction && {
      systemInstruction: { parts: [{ text: systemInstruction }] },
    }),
    ...(isJson && {
      generationConfig: { responseMimeType: "application/json" },
    }),
  };

  let delay = BASE_DELAY_MS;
  let lastError = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const message =
          errBody?.error?.message ||
          `AI request failed (HTTP ${response.status}). Please try again.`;

        const retryable = [429, 500, 502, 503, 504].includes(response.status);
        if (!retryable || attempt === MAX_RETRIES - 1) {
          const err = new Error(message);
          err.status = response.status;
          throw err;
        }

        const retryAfterMs = parseRetryAfterMs(response.headers.get("retry-after"));
        const minMs = MIN_RETRY_DELAY_MS[response.status] || BASE_DELAY_MS;
        const jitterMs = Math.floor(Math.random() * 500);
        const waitMs = Math.max(minMs, delay, retryAfterMs || 0) + jitterMs;

        await sleep(waitMs);
        delay *= 2;
        continue;
      }

      const result = await response.json();
      return result?.candidates?.[0]?.content?.parts?.[0]?.text || "";

    } catch (err) {
      // Network / CORS errors (TypeError from fetch)
      if (isNetworkError(err) && !err.status) {
        if (attempt === MAX_RETRIES - 1) {
          throw new Error(
            "Network error reaching AI service. Check your internet connection and try again.",
          );
        }
        const jitterMs = Math.floor(Math.random() * 250);
        await sleep(delay + jitterMs);
        delay *= 2;
        lastError = err;
        continue;
      }
      throw err; // rethrow errors that already have a status
    }
  }

  throw lastError || new Error("Unable to process this request right now. Please try again.");
}

// ─── Gemini File API upload ────────────────────────────────────────────────────

/**
 * Upload a binary file (PDF, image, etc.) to the Gemini File API and return
 * the hosted file URI.  The URI can then be referenced in generateContent
 * requests via `fileData: { mimeType, fileUri }` instead of sending raw
 * base64 inline, which avoids 413 / 503 errors on large payloads.
 */
async function uploadToGeminiFileApi(file, apiKey) {
  const mimeType = resolveMimeType(file);
  const arrayBuffer = await readAsArrayBuffer(file);

  // Build a multipart/related body: JSON metadata part + raw file bytes part.
  const boundary = "GeminiBoundary" + Date.now().toString(36);
  const metaJson = JSON.stringify({ file: { display_name: file.name } });
  const encoder = new TextEncoder();

  const headerBytes = encoder.encode(
    `--${boundary}\r\nContent-Type: application/json; charset=utf-8\r\n\r\n` +
    `${metaJson}\r\n--${boundary}\r\nContent-Type: ${mimeType}\r\n\r\n`,
  );
  const footerBytes = encoder.encode(`\r\n--${boundary}--`);
  const fileBytes = new Uint8Array(arrayBuffer);

  const body = new Uint8Array(
    headerBytes.byteLength + fileBytes.byteLength + footerBytes.byteLength,
  );
  body.set(headerBytes, 0);
  body.set(fileBytes, headerBytes.byteLength);
  body.set(footerBytes, headerBytes.byteLength + fileBytes.byteLength);

  const resp = await fetch(
    `https://generativelanguage.googleapis.com/upload/v1beta/files?key=${apiKey}`,
    {
      method: "POST",
      headers: {
        "X-Goog-Upload-Protocol": "multipart",
        "Content-Type": `multipart/related; boundary=${boundary}`,
      },
      body,
    },
  );

  if (!resp.ok) {
    const errText = await resp.text().catch(() => "");
    const err = new Error(
      `File upload to Gemini failed (${resp.status})${errText ? ": " + errText : ""}`,
    );
    err.status = resp.status;
    throw err;
  }

  const info = await resp.json();
  const uri = info?.file?.uri;
  if (!uri) {
    throw new Error("Gemini File API did not return a file URI. Please try again.");
  }

  // Wait for the file to finish processing (usually instant; a few seconds for large files).
  return pollUntilActive(uri, apiKey);
}

/**
 * Poll the File API until the uploaded file reaches ACTIVE state.
 * Times out gracefully after maxMs and proceeds anyway (most files
 * activate within a second or two).
 */
async function pollUntilActive(fileUri, apiKey, maxMs = 30_000) {
  // URI format: https://generativelanguage.googleapis.com/v1beta/files/<name>
  const fileName = fileUri.split("/").pop();
  const statusUrl =
    `https://generativelanguage.googleapis.com/v1beta/files/${fileName}?key=${apiKey}`;
  const deadline = Date.now() + maxMs;

  while (Date.now() < deadline) {
    const resp = await fetch(statusUrl).catch(() => null);
    if (!resp || !resp.ok) return fileUri; // proceed optimistically on error

    const info = await resp.json().catch(() => ({}));
    if (info?.file?.state === "ACTIVE") return fileUri;
    if (info?.file?.state === "FAILED") {
      throw new Error(
        "Gemini could not process the uploaded file. Try a different file or format.",
      );
    }
    await sleep(2000);
  }

  return fileUri; // proceed after timeout
}

// ─── Text trimming ─────────────────────────────────────────────────────────────

/**
 * Trim newline-delimited text to at most maxRows lines so that large CSV/XLSX
 * exports don't bloat the request payload.  The first row (header) is always
 * kept; trimming happens from the end.
 */
function trimRows(text, maxRows) {
  const lines = text.split("\n");
  if (lines.length <= maxRows) return text;
  const omitted = lines.length - maxRows;
  return (
    lines.slice(0, maxRows).join("\n") +
    `\n[... ${omitted} more row${omitted === 1 ? "" : "s"} omitted]`
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isNetworkError(err) {
  return (
    err instanceof TypeError ||
    /failed to fetch|networkerror/i.test(String(err?.message || ""))
  );
}

function parseRetryAfterMs(value) {
  if (!value) return null;
  const asSeconds = Number(value);
  if (Number.isFinite(asSeconds)) return Math.max(0, asSeconds * 1000);
  const asDate = Date.parse(value);
  if (Number.isFinite(asDate)) return Math.max(0, asDate - Date.now());
  return null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveMimeType(file) {
  if (file.type) return file.type;
  const lower = String(file.name || "").toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  return "application/octet-stream";
}

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.readAsText(file);
    r.onload = () => resolve(r.result);
    r.onerror = reject;
  });
}

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.readAsArrayBuffer(file);
    r.onload = () => resolve(r.result);
    r.onerror = reject;
  });
}

async function readExcelAsText(file) {
  const XLSX = await import("xlsx");
  const arrayBuffer = await readAsArrayBuffer(file);
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  return workbook.SheetNames.map((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const csv = XLSX.utils.sheet_to_csv(sheet);
    return `--- Sheet: ${sheetName} ---\n${csv}`;
  }).join("\n\n");
}
